import { useOktaAuth } from '@okta/okta-react';
import {
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ErrorPage as ErrorScreen } from '~/components/ErrorPage';
import { bff } from '~/lib/bff';
import type { User } from '~/modules/auth';
import { BrandContext } from '~/modules/branding';
import { LoadingScreen } from '~/modules/navigation/components/LoadingScreen';

import type { UserProfile } from '../types';
import { getUserProfile } from './utils/getUserProfile';
import { WmgSessionContext } from './WmgSessionContext';

function WmgSessionProvider({ children }: PropsWithChildren) {
  const brand = useContext(BrandContext);
  const { authState, oktaAuth } = useOktaAuth();
  const [error, setError] = useState<string | undefined>();
  const [user, setUser] = useState<User | undefined>(undefined);
  const [userProfile, setUserProfile] = useState<UserProfile | undefined>(
    undefined,
  );
  const isFetchingPermissions = useRef<boolean>(false);

  const oktaToken = authState?.accessToken?.accessToken;
  const oktaUserId = authState?.accessToken?.claims?.uid as string;

  const authenticateUser = useCallback(
    async (token?: string, userId?: string) => {
      if (!token || !userId || isFetchingPermissions.current) {
        return;
      }

      setError(undefined);
      isFetchingPermissions.current = true;

      try {
        const fetchedUser = await bff.getUser(token);
        const fetchedUserInfo = await bff.getUserInfo(token, userId);

        // We know that for a WMG user the fetchedUser comes back with empty
        // values (except userLocale). So get the user info from the Okta
        // token instead.
        if (brand.name === 'WMG') {
          const oktaUser = await oktaAuth.getUser();

          setUser((prevState) => {
            return {
              ...(prevState || ({} as User)),
              name: oktaUser.name ?? '',
              email: oktaUser.email ?? '',
              isInternal: true, // WMG users are always internal.
              userId: fetchedUserInfo.userId,
              permissions: fetchedUser.permissions,
            };
          });
        } else {
          setUser((prevState) => {
            return {
              ...(prevState || ({} as User)),
              name: fetchedUser.name,
              email: fetchedUserInfo.email,
              permissions: fetchedUser.permissions,
              features: fetchedUserInfo.userFeatures,
              isInternal: fetchedUserInfo.isInternal,
              userId: fetchedUserInfo.userId,
              userType: fetchedUserInfo.userType,
            };
          });
        }
      } catch (e) {
        setError((e as Error).message || 'Unexpected error');
      } finally {
        isFetchingPermissions.current = false;
      }
    },
    [brand.name, oktaAuth],
  );

  useEffect(() => {
    authenticateUser(oktaToken, oktaUserId);
  }, [authenticateUser, oktaToken, oktaUserId]);

  useEffect(() => {
    if (authState) {
      setUserProfile(getUserProfile(authState));
    }
  }, [authState]);

  const defaultUser: User = useMemo(() => {
    return {
      name: user?.name ?? '',
      email: user?.email ?? '',
      permissions: user?.permissions ?? {},
      isInternal: user?.isInternal ?? false,
      features: user?.features ?? [],
      profile: userProfile,
      userId: user?.userId ?? undefined,
      userType: user?.userType ?? undefined,
    };
  }, [user, userProfile]);

  const sessionValue = useMemo(() => {
    return { user: defaultUser, token: oktaToken ?? '' };
  }, [defaultUser, oktaToken]);

  if (error) {
    return (
      <ErrorScreen
        message={error}
        onRetry={() => authenticateUser(oktaToken, oktaUserId)}
      />
    );
  }

  if (!oktaToken || !user || isFetchingPermissions.current) {
    return <LoadingScreen />;
  }

  return (
    <WmgSessionContext.Provider value={sessionValue}>
      {children}
    </WmgSessionContext.Provider>
  );
}

WmgSessionProvider.displayName = 'WmgSessionProvider';

export { WmgSessionContext, WmgSessionProvider };
